/**
 * Client for the certification callables (functions/src/certificates).
 *
 * Grading, lesson completion, enrollment and certificates are all decided on
 * the server; firestore.rules deny the browser direct writes to the records
 * they depend on. Anything in the app that enrolls, grades, completes or
 * certifies should go through here.
 */
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import type { QuestionMedia } from '@shared/question-media';

async function call<Req, Res>(name: string, data: Req): Promise<Res> {
  const result = await httpsCallable<Req, Res>(functions, name)(data);
  return result.data;
}

export interface CourseOutlineLesson {
  id: string;
  title: string;
  description: string | null;
  order: number;
  durationMinutes: number | null;
  hasVideo: boolean;
  isPreview: boolean;
  isPublished: boolean;
}

export interface CourseOutline {
  courseId: string;
  modules: Array<{
    id: string;
    title: string;
    description: string | null;
    order: number;
    estimatedDurationMinutes: number | null;
    isPublished: boolean;
    lessons: CourseOutlineLesson[];
  }>;
}

export interface LearnerLesson {
  id: string;
  title: string;
  description: string | null;
  content: string;
  videoUrl: string | null;
  durationMinutes: number | null;
  moduleId: string | null;
  moduleTitle: string | null;
  courseId: string;
  order: number;
  isPreview: boolean;
  isPublished: boolean;
  attachments: unknown[];
}

export interface CompletionRequirements {
  lessonsRequired: number;
  lessonsCompleted: number;
  quizzesRequired: number;
  quizzesPassedRequired: number;
  quizzesPassed: number;
  examsRequired: number;
  examsPassed: number;
}

export interface CompletionStatus {
  courseId: string;
  userId: string;
  enrolled: boolean;
  enrollmentId: string | null;
  isComplete: boolean;
  completionPercentage: number;
  requirements: CompletionRequirements;
  reason?: string;
  certificateId: string | null;
}

export interface CompleteCourseResult {
  success: boolean;
  isComplete: boolean;
  completionPercentage: number;
  requirements: CompletionRequirements;
  reason?: string;
  enrollmentId: string | null;
  certificateId: string | null;
  certificateCreated: boolean;
  message: string;
}

export interface PublicQuestion {
  id: string;
  type: string;
  question: string;
  options: string[];
  points: number;
  order: number;
  /** Open answer: what it accepts (text, photos, voice, video) and the longest recording, in seconds. */
  accepts?: ('text' | 'image' | 'audio' | 'video' | 'file')[];
  maxSeconds?: number;
  /** A picture, video or audio clip shown with the question. */
  media?: QuestionMedia;
  /** Multiple choice: a picture per option (null where there is none). */
  optionMedia?: (QuestionMedia | null)[];
}

export interface QuizMeta {
  id: string;
  title: string;
  description: string | null;
  courseId: string;
  moduleId: string | null;
  lessonId: string | null;
  passingScore: number;
  timeLimit: number | null;
  maxAttempts: number | null;
  isActive: boolean;
  questionsCount: number;
}

export interface QuizForTaking {
  quiz: QuizMeta;
  questions: PublicQuestion[];
  canTake: boolean;
  /** The course's instructor or an admin, not enrolled: may read it, not submit it. */
  staffPreview?: boolean;
  /** A lesson's quiz opens once that lesson (and its video) is completed. */
  lockedUntil?: { lessonId: string; title: string } | null;
  reason?: string;
  previousAttempts: Array<{
    id: string;
    score: number;
    passed: boolean;
    /** Essays not marked yet: no final score or result. */
    awaitingMarking?: boolean;
    completedAt: string | null;
    /** The instructor's mark and feedback on this attempt's essay answers. */
    essayFeedback?: Array<{ question: string; feedback: string | null; score: number | null; passed: boolean | null }>;
  }>;
}

export interface QuizAttemptResult {
  attemptId: string;
  quizId: string;
  courseId: string;
  score: number;
  passed: boolean;
  passingScore: number;
  timedOut: boolean;
  /** It has essays: the instructor marks it; no score or result until then. */
  awaitingMarking?: boolean;
  totalQuestions: number;
  correctAnswers: number;
  results: Array<{ questionId: string; isCorrect: boolean; ungraded?: boolean }>;
  attemptNumber: number;
  courseComplete: boolean;
  certificateId: string | null;
}

export interface ExamForTaking {
  id: string;
  title: string;
  description: string | null;
  instructions: string | null;
  courseId: string | null;
  timeLimit: number | null;
  passingScore: number;
  maxAttempts: number | null;
  attemptsUsed: number;
  canTake: boolean;
  shuffleQuestions: boolean;
  questions: PublicQuestion[];
}

export interface ExamAttemptResult {
  attemptId: string;
  score: number;
  passed: boolean;
  awaitingMarking?: boolean;
  totalQuestions: number;
  correctAnswers: number;
  passingScore: number;
  detailedResults: Array<{ questionId: string; isCorrect: boolean }>;
  courseComplete: boolean;
  certificateGenerated: boolean;
  certificateId: string | null;
}

export interface CourseAssessments {
  courseId: string;
  quizzes: Array<QuizMeta & { orderIndex: number; passed: boolean; awaitingMarking?: boolean }>;
  exams: Array<{
    id: string;
    title: string;
    description: string | null;
    timeLimit: number | null;
    passingScore: number;
    questionsCount: number;
    passed: boolean;
    awaitingMarking?: boolean;
  }>;
}

export interface CertificateVerification {
  found: boolean;
  valid: boolean;
  status?: 'active' | 'revoked';
  verificationCode?: string;
  recipientName?: string | null;
  courseName?: string | null;
  instructorName?: string | null;
  issuedAt?: string | null;
  revokedAt?: string | null;
}

export interface CertificateAdminData {
  courses: Array<{ id: string; title: string; price: number }>;
  courseId: string | null;
  students: Array<{ userId: string; name: string; email: string | null; enrollmentId: string; entitled: boolean }>;
  certificates: Array<{
    id: string;
    userId: string;
    status: 'active' | 'revoked';
    recipientName: string | null;
    verificationCode: string | null;
    issuedAt: string | null;
    source: string | null;
    revocationReason: string | null;
  }>;
}

export interface EssayResponse {
  kind: 'quiz' | 'exam';
  attemptId: string;
  userId: string;
  learner: string;
  assessment: string;
  assessmentId: string;
  submittedAt: number | null;
  courseId: string;
  courseTitle: string;
  /** The lesson the quiz belongs to (quizzes only). */
  lessonId: string | null;
  lessonTitle: string | null;
  moduleId: string | null;
  questionId: string;
  question: string;
  answer: string;
  /** A written answer, or a short answer that matched no accepted answer or key phrase. */
  answerKind?: 'essay' | 'shortAnswer' | 'media';
  /** A short answer: how close it came to the key (0-1); null for older answers. */
  answerConfidence?: number | null;
  /** What the question is worth: the instructor marks out of this (null: unknown, marked 0-100). */
  points?: number | null;
  /** A short answer the instructor marks every time. */
  answerByInstructor?: boolean;
  /** An open answer's photos, recordings and links (an upload's link lasts an hour). */
  attachments?: Array<{ kind: 'image' | 'audio' | 'video' | 'file' | 'link'; url: string | null; mimeType: string | null; durationSec?: number | null; name?: string | null }>;
  feedback: string | null;
  reviewedAt: number | null;
  /** This essay's mark, once given: 0-100 and pass/fail. */
  score: number | null;
  passed: boolean | null;
  /** The whole attempt: still awaiting marking, or its final score and result. */
  attemptAwaitingMarking: boolean;
  attemptScore: number | null;
  attemptPassed: boolean | null;
}

export interface EssayResponsesData {
  /** The caller's courses, with how many answers each has and how many await marking. */
  courses: Array<{ id: string; title: string; total: number; awaiting: number }>;
  courseId: string | null;
  responses: EssayResponse[];
}

export interface QuizResultsData {
  quiz: { id: string; title: string; passingScore: number; courseId?: string | null };
  results: Array<{
    userId: string;
    name: string;
    attempts: number;
    bestScore: number;
    lastScore: number;
    passed: boolean;
    lastAttemptAt: number | null;
    /** An attempt is waiting for its essays to be marked. */
    awaitingMarking?: boolean;
  }>;
}

export type EnrollmentKindName = 'paid' | 'free' | 'added' | 'refunded' | 'disputed' | 'revoked' | 'unpaid';

export interface Paged<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  pageCount: number;
}

export interface InstructorStudent {
  enrollmentId: string;
  userId: string;
  name: string;
  email: string | null;
  courseId: string;
  courseTitle: string;
  kind: EnrollmentKindName;
  /** Still has access (paid, free or added by the instructor). */
  current: boolean;
  enrolledAt: number | null;
  progress: number;
  completed: boolean;
  certificate: {
    id: string;
    status: 'active' | 'revoked';
    source: 'completion' | 'manual';
    issuedAt: number | null;
    verificationCode: string | null;
  } | null;
}

export interface InstructorStudentsData extends Paged<InstructorStudent> {
  counts: { current: number; former: number; paid: number; free: number; added: number; certified: number };
  courses: Array<{ id: string; title: string; price: number; students: number }>;
}

export interface InstructorCertificate {
  id: string;
  userId: string;
  recipientName: string;
  courseId: string;
  courseTitle: string;
  courseDeleted: boolean;
  issuedAt: number | null;
  /** Earned by completing the course, or issued by hand. */
  source: 'completion' | 'manual';
  status: 'active' | 'revoked';
  verificationCode: string | null;
  revocationReason: string | null;
  revokedAt: number | null;
  /** A 10-minute link to the certificate PDF, for the preview. */
  previewUrl: string | null;
}

export interface InstructorCertificatesData extends Paged<InstructorCertificate> {
  counts: { active: number; revoked: number; thisMonth: number; manual: number };
  courses: Array<{ id: string; title: string; certificates: number }>;
}

export const certificationApi = {
  /** Instructor panel -> Enrollments: students across the caller's courses, paginated. */
  listInstructorStudents: (q: { courseId?: string | null; show?: 'current' | 'former' | 'all'; search?: string; page?: number; pageSize?: number }) =>
    call<typeof q, InstructorStudentsData>('listInstructorStudents', q),

  /** Instructor panel -> Certificates: certificates for the caller's courses, paginated, with PDF previews. */
  listInstructorCertificates: (q: { courseId?: string | null; status?: 'active' | 'revoked' | 'all'; search?: string; page?: number; pageSize?: number }) =>
    call<typeof q, InstructorCertificatesData>('listInstructorCertificates', q),

  getCertificateAdminData: (courseId?: string) =>
    call<{ courseId?: string }, CertificateAdminData>('getCertificateAdminData', { courseId }),

  /** Public: modules and lesson titles, never lesson bodies. */
  getCourseOutline: (courseId: string) =>
    call<{ courseId: string }, CourseOutline>('getCourseOutline', { courseId }),

  /** The whole lesson, for someone enrolled (or the instructor, or a preview lesson). */
  getLessonForLearner: (courseId: string, lessonId: string) =>
    call<{ courseId: string; lessonId: string }, LearnerLesson>('getLessonForLearner', { courseId, lessonId }),

  getEssayResponses: (courseId?: string) =>
    call<{ courseId?: string }, EssayResponsesData>('getEssayResponses', { courseId }),

  /** `placement`: an unsaved placement to preview (null: none); left out, the template's own. */
  previewCertificate: (r: { imageUrl?: string; artworkHasWording?: boolean; courseId?: string; instructorName?: string; signatureUrl?: string; placement?: unknown }) =>
    call<typeof r, { pdfBase64: string; artworkUsed: boolean }>('previewCertificate', r),

  getQuizResults: (quizId: string) =>
    call<{ quizId: string }, QuizResultsData>('getQuizResults', { quizId }),

  reviewEssayResponse: (r: { kind: 'quiz' | 'exam'; attemptId: string; questionId: string; feedback: string; score: number; passed: boolean }) =>
    call<typeof r, {
      success: boolean;
      attemptMarked: boolean;
      essaysMarked: number;
      essayCount: number;
      score: number | null;
      passed: boolean | null;
      courseComplete: boolean;
    }>('reviewEssayResponse', r),

  completeCourse: (courseId: string) =>
    call<{ courseId: string }, CompleteCourseResult>('completeCourse', { courseId }),

  getCompletionStatus: (courseId: string, userId?: string) =>
    call<{ courseId: string; userId?: string }, CompletionStatus>('getCourseCompletionStatus', { courseId, userId }),

  markLessonCompleted: (lessonId: string, courseId?: string, timeSpent?: number, watchedPercent?: number) =>
    call<
      { lessonId: string; courseId?: string; timeSpent?: number; watchedPercent?: number },
      { lessonId: string; courseId: string; progress: number; isCourseComplete: boolean; certificateId: string | null }
    >('markLessonCompleted', { lessonId, courseId, timeSpent, watchedPercent }),

  enrollInFreeCourse: (courseId: string) =>
    call<{ courseId: string }, { enrollmentId: string; alreadyEnrolled: boolean }>('enrollInFreeCourse', { courseId }),

  instructorEnrollStudent: (courseId: string, userId: string) =>
    call<{ courseId: string; userId: string }, { enrollmentId: string; alreadyEnrolled: boolean }>(
      'instructorEnrollStudent',
      { courseId, userId }
    ),

  issueCertificate: (courseId: string, userId: string, templateId?: string | null) =>
    call<
      { courseId: string; userId: string; templateId?: string | null },
      { certificateId: string; created: boolean; verificationCode: string }
    >('issueCourseCertificate', { courseId, userId, templateId }),

  revokeCertificate: (certificateId: string, reason?: string) =>
    call<{ certificateId: string; reason?: string }, { certificateId: string; status: 'revoked' }>(
      'revokeCourseCertificate',
      { certificateId, reason }
    ),

  verifyCertificate: (code: string) =>
    call<{ code: string }, CertificateVerification>('verifyCertificate', { code }),

  getQuizForTaking: (quizId: string) => call<{ quizId: string }, QuizForTaking>('getQuizForTaking', { quizId }),

  submitQuizAttempt: (quizId: string, answers: Record<string, unknown>, timeSpentSeconds: number) =>
    call<{ quizId: string; answers: Record<string, unknown>; timeSpentSeconds: number }, QuizAttemptResult>(
      'submitQuizAttempt',
      { quizId, answers, timeSpentSeconds }
    ),

  getExamForTaking: (examId: string) => call<{ examId: string }, ExamForTaking>('getExamForTaking', { examId }),

  submitExamAttempt: (examId: string, answers: Record<string, unknown>, timeSpent: number) =>
    call<{ examId: string; answers: Record<string, unknown>; timeSpent: number }, ExamAttemptResult>(
      'submitExamAttempt',
      { examId, answers, timeSpent }
    ),

  getCourseAssessments: (courseId: string) =>
    call<{ courseId: string }, CourseAssessments>('getCourseAssessments', { courseId }),
};

/** Public link for a certificate's verification page. */
export function certificateVerificationUrl(code: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://dancerealmx.com';
  return `${origin}/verify/${encodeURIComponent(code)}`;
}
